import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { usePost } from '../hooks/usePost'
import { usePut } from '../hooks/usePut'
import { useGet } from '../hooks/useGet'
import { RadHeader } from '../common/RadHeader'
import { RadForm } from '../common/RadForm'
import { RadInput } from '../common/RadInput'
import { RadFormField } from '../common/RadFormField'
import { RadButton } from '../common/RadButton'
import { RadSpaceBetween } from '../common/RadSpaceBetween'
import { RadContainer } from '../common/RadContainer'
import { RadTextarea } from '../common/RadTextarea'
import { RadCheckbox } from '../common/RadCheckbox'
import { RadAttributeEditor } from '../common/RadAttributeEditor'
import { RadSelect } from '../common/RadSelect'
import { v4 as uuidv4 } from 'uuid'

export function CollectionEditContent ({ collection, formValues, setFormValues, modal = false }) {
  const navigate = useNavigate()
  const [resourceFilterText, setResourceFilterText] = useState('')
  const { data: resourceOptions } = useGet(`/api/option/resource?search=${resourceFilterText}&selected=${formValues?.resources?.filter(x => x.resourceId).map(x => x.resourceId).join(',') || ''}`)

  useEffect(() => {
    const defaultFormValues = {
      name: '',
      subtitle: '',
      description: '',
      isFeatured: false,
      featuredBlogPostName: '',
      featuredBlogPostUrl: '',
      resources: []
    }

    if (collection) {
      setFormValues({
        ...collection,
        resources: collection.resources?.map(cr => ({
          id: cr.id,
          resourceId: cr.resource.id,
          resourceName: cr.resource.title
        })) || []
      })
    } else {
      setFormValues(defaultFormValues)
    }
  }, [collection, setFormValues])

  const create = usePost('/api/collection', formValues, (resp) => { navigate(`/collection?collectionId=${resp.id}`) })
  const update = usePut(`/api/collection/${collection?.id}`, formValues, (resp) => { navigate(`/collection/${collection?.id}`) })

  async function saveChanges () {
    if (collection?.id != null) { update() } else { create() }
  }

  async function cancel () {
    if (collection?.id != null) {
      navigate(`/collection/${collection?.id}`)
    } else {
      navigate('/collection')
    }
  }

  if (formValues != null && resourceOptions != null) {
    return (
      <form onSubmit={e => e.preventDefault()}>
        <RadForm
          actions={
            !modal
              ? (
                <RadSpaceBetween direction='horizontal' size='xs'>
                  <RadButton variant='link' onClick={cancel}>Cancel</RadButton>
                  <RadButton formAction='submit' variant='primary' onClick={saveChanges}>Save Changes</RadButton>
                </RadSpaceBetween>)
              : null
          }
        >
          <RadSpaceBetween size='l'>
            <RadContainer
              header={
                <RadHeader variant='h2'>
                  Details
                </RadHeader>
              }
            >
              <RadSpaceBetween size='l'>
                <RadFormField label='Name *' field='name'>
                  <RadInput
                    placeholder='Enter collection name'
                    ariaRequired
                    value={formValues.name}
                    onChange={({ detail }) => setFormValues({ ...formValues, name: detail.value })}
                  />
                </RadFormField>

                <RadFormField label='Subtitle *' field='subtitle'>
                  <RadInput
                    placeholder='Enter collection subtitle'
                    ariaRequired
                    value={formValues.subtitle}
                    onChange={({ detail }) => setFormValues({ ...formValues, subtitle: detail.value })}
                  />
                </RadFormField>

                <RadFormField label='Description *' field='description'>
                  <RadTextarea
                    placeholder='Enter collection description'
                    ariaRequired
                    value={formValues.description}
                    onChange={({ detail }) => setFormValues({ ...formValues, description: detail.value })}
                    rows={4}
                  />
                </RadFormField>

                <RadFormField label='Featured Collection'>
                  <RadCheckbox
                    checked={formValues.isFeatured}
                    onChange={({ detail }) => setFormValues({ ...formValues, isFeatured: detail.checked })}
                  >
                    Mark as featured collection
                  </RadCheckbox>
                </RadFormField>

                <RadFormField label='Featured Blog Post Name' field='featuredBlogPostName'>
                  <RadInput
                    placeholder='Enter blog post name (optional)'
                    value={formValues.featuredBlogPostName || ''}
                    onChange={({ detail }) => setFormValues({ ...formValues, featuredBlogPostName: detail.value })}
                  />
                </RadFormField>

                <RadFormField label='Featured Blog Post URL' field='featuredBlogPostUrl'>
                  <RadInput
                    placeholder='Enter blog post URL (optional)'
                    value={formValues.featuredBlogPostUrl || ''}
                    onChange={({ detail }) => setFormValues({ ...formValues, featuredBlogPostUrl: detail.value })}
                  />
                </RadFormField>
              </RadSpaceBetween>
            </RadContainer>

            <RadContainer
              header={
                <RadHeader variant='h2'>
                  Resources *
                </RadHeader>
              }
            >
              <RadAttributeEditor
                onAddButtonClick={() => setFormValues({
                  ...formValues,
                  resources: [...formValues.resources, { uuid: uuidv4(), resourceId: '', resourceName: '' }]
                })}
                onRemoveButtonClick={({ detail: { itemIndex } }) => {
                  const resources = [...formValues.resources]
                  resources.splice(itemIndex, 1)
                  setFormValues({ ...formValues, resources })
                }}
                items={formValues.resources}
                addButtonText='Add resource'
                definition={[
                  {
                    label: 'Resource *',
                    control: item => {
                      const filteredOptions = resourceOptions?.filter((x) => !formValues.resources.map((y) => y.resourceId?.toString()).includes(x.value)) || []
                      return (
                        <RadFormField field={`resources.${item.id ?? item.uuid}.resourceId`}>
                          <RadSelect
                            filteringType='manual'
                            selectedOption={resourceOptions?.find(x => x.value === item.resourceId?.toString())}
                            onChange={({ detail }) => {
                              const resources = formValues.resources
                              const resource = formValues.resources.find((x) => x.uuid === (item.uuid ?? '') || x.id === (item.id ?? ''))
                              resource.resourceId = parseInt(detail.selectedOption.value)
                              resource.resourceName = detail.selectedOption.label
                              setFormValues({ ...formValues, resources })
                            }}
                            onLoadItems={({ detail }) => {
                              setResourceFilterText(detail.filteringText)
                            }}
                            options={filteredOptions}
                            enteredTextLabel={value => value}
                            selectedAriaLabel='Selected'
                            placeholder='Choose a resource'
                            empty='No matches found'
                          />
                        </RadFormField>
                      )
                    }
                  }
                ]}
                removeButtonText='Remove'
                empty='No resources added'
              />
            </RadContainer>
          </RadSpaceBetween>
        </RadForm>
      </form>
    )
  }
}
